import { tripPDFService } from './tripPDFService';
import { tripService } from './tripService';

class EmailService {
  constructor() {
    this.emailJSPublicKey = import.meta.env?.VITE_EMAILJS_PUBLIC_KEY;
    this.emailJSServiceId = import.meta.env?.VITE_EMAILJS_SERVICE_ID;
    this.emailJSTemplateId = import.meta.env?.VITE_EMAILJS_TEMPLATE_ID;
  }

  async sendTripPDF(tripData, recipientEmail) {
    try {
      // Check if EmailJS is configured
      if (!this.emailJSPublicKey || !this.emailJSServiceId || !this.emailJSTemplateId) {
        console.warn('EmailJS not configured, falling back to direct download');
        return await this.showDownloadFallback(tripData);
      }

      // Generate PDF blob
      const pdfBlob = await tripPDFService?.generatePDF(tripData);
      
      // For EmailJS, we need to convert to base64
      const pdfBase64 = await this.blobToBase64(pdfBlob);

      // Prepare email template data with enhanced information
      const templateParams = {
        to_email: recipientEmail,
        to_name: recipientEmail?.split('@')?.[0] || 'Adventure Seeker',
        trip_title: `${tripData?.featuredParks?.[0] || 'State Park'} Adventure`,
        trip_destination: tripData?.featuredParks?.join(', ') || 'Multiple Parks',
        trip_duration: tripData?.duration || '3 Days',
        trip_activities: tripData?.activityTypes?.join(', ') || 'Outdoor Activities',
        estimated_cost: tripData?.estimatedCost || 'Moderate Budget',
        weather_forecast: tripData?.weatherForecast || 'Check local conditions',
        trip_summary: this.generateTripSummary(tripData),
        pdf_attachment: pdfBase64,
        pdf_filename: `${this.sanitizeFilename(tripData?.featuredParks?.[0] || tripData?.title || 'trip_plan')}.pdf`,
        sender_name: 'ParkTrip Planner AI AI Team',
        current_year: new Date()?.getFullYear()
      };

      // Load EmailJS dynamically to reduce bundle size
      const emailjs = await import('emailjs-com');

      // Send email with PDF attachment
      const response = await emailjs?.default?.send(
        this.emailJSServiceId,
        this.emailJSTemplateId,
        templateParams,
        this.emailJSPublicKey
      );

      console.log('Email sent successfully:', response);

      return {
        success: true,
        messageId: response?.text,
        message: 'Trip PDF sent successfully! Check your inbox.'
      };

    } catch (error) {
      console.error('Error sending trip PDF:', error);
      
      // Enhanced fallback handling
      if (error?.message?.includes('not configured') || error?.status === 400) {
        return await this.showDownloadFallback(tripData);
      }
      
      // For other errors, still offer fallback but mention the issue
      console.warn('Email service failed, providing download fallback');
      return await this.showDownloadFallback(tripData, error?.message);
    }
  }

  async sendTripPDFWithTracking(userId, tripId, email, pdfUrl, tripData) {
    try {
      // Existing email sending logic...
      const emailResult = await this.sendEmail({
        to: email,
        subject: `Your Trip Itinerary - ${tripData?.destination || 'Trip Details'}`,
        html: this.generateTripEmailHTML(tripData, pdfUrl)
      });

      // ✅ NEW: Track email sending activity
      await tripService?.logActivity(
        userId,
        tripId,
        'email_sent',
        `PDF report emailed to ${email}`,
        {
          email: email,
          pdf_url: pdfUrl,
          trip_destination: tripData?.destination
        }
      );

      return emailResult;
    } catch (error) {
      console.error('Error sending trip PDF email:', error);
      throw error;
    }
  }

  async shareTripDetails(userId, tripId, email, message, tripData, senderName) {
    try {
      // Existing share logic...
      const shareResult = await this.sendEmail({
        to: email,
        subject: `${senderName} shared a trip with you`,
        html: this.generateShareEmailHTML(tripData, message, senderName)
      });

      // ✅ NEW: Track trip sharing activity
      await tripService?.logActivity(
        userId,
        tripId,
        'shared',
        `Shared trip details with ${email}`,
        {
          shared_with: email,
          message: message,
          trip_destination: tripData?.destination
        }
      );

      return shareResult;
    } catch (error) {
      console.error('Error sharing trip details:', error);
      throw error;
    }
  }

  generateTripSummary(tripData) {
    const parts = [];
    
    if (tripData?.featuredParks?.length > 0) {
      parts?.push(`Parks: ${tripData?.featuredParks?.slice(0, 2)?.join(', ')}`);
    }
    
    if (tripData?.duration) {
      parts?.push(`Duration: ${tripData?.duration}`);
    }
    
    if (tripData?.activityTypes?.length > 0) {
      parts?.push(`Activities: ${tripData?.activityTypes?.slice(0, 3)?.join(', ')}`);
    }
    
    if (tripData?.estimatedCost) {
      parts?.push(`Budget: ${tripData?.estimatedCost}`);
    }
    
    return parts?.join(' • ') || 'Customized state park adventure itinerary';
  }

  sanitizeFilename(name) {
    return name?.replace(/[^a-z0-9]/gi, '_')?.toLowerCase() || 'trip_plan';
  }

  async showDownloadFallback(tripData, originalError) {
    try {
      // Generate PDF and trigger download instead of email
      const pdfBlob = await tripPDFService?.generatePDF(tripData);
      const url = URL?.createObjectURL(pdfBlob);
      const link = document?.createElement('a');
      link.href = url;
      link.download = `${this.sanitizeFilename(tripData?.featuredParks?.[0] || tripData?.title || 'trip_plan')}.pdf`;
      
      // Ensure link is added to DOM for some browsers
      document?.body?.appendChild(link);
      link?.click();
      document?.body?.removeChild(link);
      
      // Clean up the URL object
      setTimeout(() => URL?.revokeObjectURL(url), 100);

      return {
        success: true,
        message: originalError 
          ? 'Email service temporarily unavailable. Your PDF has been downloaded instead!'
          : 'PDF downloaded successfully! Email service is not configured, so we\'ve downloaded the PDF to your device.',
        fallback: true
      };
    } catch (error) {
      console.error('Fallback download failed:', error);
      throw new Error('Failed to generate PDF. Please try again or contact support.');
    }
  }

  async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove the data URL prefix to get just the base64 string
        const base64 = reader?.result?.split(',')?.[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to convert PDF to base64'));
      reader?.readAsDataURL(blob);
    });
  }

  // Enhanced text email method with better formatting
  async sendTextEmail(recipientEmail, subject, message, tripContext = null) {
    try {
      if (!this.emailJSPublicKey || !this.emailJSServiceId) {
        throw new Error('Email service not configured');
      }

      const emailjs = await import('emailjs-com');
      
      const templateParams = {
        to_email: recipientEmail,
        to_name: recipientEmail?.split('@')?.[0] || 'Friend',
        subject: subject,
        message: message,
        sender_name: 'ParkTrip Planner AI AI Team',
        current_year: new Date()?.getFullYear()
      };

      // Add trip context if provided
      if (tripContext) {
        templateParams.trip_context = this.generateTripSummary(tripContext);
      }

      const response = await emailjs?.default?.send(
        this.emailJSServiceId,
        this.emailJSTemplateId,
        templateParams,
        this.emailJSPublicKey
      );

      return {
        success: true,
        messageId: response?.text,
        message: 'Email sent successfully!'
      };

    } catch (error) {
      console.error('Error sending email:', error);
      throw new Error(`Failed to send email: ${error?.message}`);
    }
  }

  // Enhanced service configuration check
  isConfigured() {
    const hasKeys = !!(this.emailJSPublicKey && this.emailJSServiceId && this.emailJSTemplateId);
    
    if (!hasKeys) {
      console.info('EmailJS not configured. Set VITE_EMAILJS_PUBLIC_KEY, VITE_EMAILJS_SERVICE_ID, and VITE_EMAILJS_TEMPLATE_ID environment variables to enable email functionality.');
    }
    
    return hasKeys;
  }

  // Method to test email configuration
  async testConfiguration() {
    if (!this.isConfigured()) {
      return { success: false, error: 'Email service not configured' };
    }

    try {
      // Test with a simple email
      const result = await this.sendTextEmail(
        'test@example.com',
        'ParkTrip Email Test',
        'This is a test email to verify EmailJS configuration.',
        null
      );
      return { success: true, message: 'Email service is working correctly' };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }
}

export const emailService = new EmailService();